import { Component, signal, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { MessageService } from '@apps/chat/message';
import {
    Action,
    GridData,
    IamService,
    now,
    uuid,
    ViewBaseComponent,
} from '@aurora';
import { FuseDrawerComponent } from '@fuse/components/drawer';
import { filter, lastValueFrom } from 'rxjs';
import { AiChatComponent } from '../shared/components/ai-chat/ai-chat.component';
import {
    AiChatUser,
    MessageRole,
} from '../shared/components/ai-chat/ai-chat.types';
import { GeneralChatService } from './general-chat.service';
import { ChatConversation, ChatMessage } from '@apps/chat';
import { ConversationService } from '@apps/chat/conversation';
import { DatePipe } from '@angular/common';

export const generalChatConversationPaginationScope: string =
    'ama::generalChatConversation';
export const conversationListPaginationScope: string = 'ama::conversationList';

@Component({
    selector: 'general-chat',
    templateUrl: './general-chat.component.html',
    styles: `
        general-chat {
            position: static;
            display: block;
            flex: none;
            width: auto;
        }
    `,
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        DatePipe,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        FuseDrawerComponent,
        AiChatComponent,
    ],
})
export class GeneralChatComponent extends ViewBaseComponent {
    // -- customizations --
    messages = signal<ChatMessage[] | null>(null);
    conversations = signal<ChatConversation[] | null>(null);
    activeConversation = signal<ChatConversation | null>(null);
    showChatList = signal<boolean>(true);
    isLoading = signal<boolean>(false);
    currentAccount = signal<any>(null);
    currentUser = signal<AiChatUser>({
        id: '1',
        name: 'Usuario',
    });

    // Variables para paginación de mensajes
    private currentMessageLimit = signal<number>(20);
    private totalMessages = signal<number>(0);
    private isLoadingMoreMessages = signal<boolean>(false);

    // Variables para paginación de conversaciones
    private currentConversationLimit = signal<number>(20);
    private totalConversations = signal<number>(0);
    private isLoadingMoreConversations = signal<boolean>(false);

    constructor(
        private readonly chatService: GeneralChatService,
        private readonly messageService: MessageService,
        private readonly conversationService: ConversationService,
        private readonly iamService: IamService,
        public readonly router: Router,
    ) {
        super();
    }

    init(): void
    {
        this.currentAccount.set(this.iamService.me);
        this.setUser(this.currentAccount());

        // create subscription to get messages
        this.messageService
            .getScopePagination(generalChatConversationPaginationScope)
            .pipe(
                filter(
                    (pagination) =>
                        pagination !== null && pagination.rows !== null
                )
            )            .subscribe((pagination: GridData<any>) => {
                // Reverse para mostrar en orden cronológico (antiguos arriba, nuevos abajo)
                this.messages.set(pagination.rows.reverse());

                // Actualizar el total de mensajes disponibles
                this.totalMessages.set(pagination.total);

                // Reset flag después de procesar
                this.isLoadingMoreMessages.set(false);
            });

        // create subscription to get conversations
        this.conversationService
            .getScopePagination(conversationListPaginationScope)
            .pipe(
                filter(
                    (pagination) =>
                        pagination !== null && pagination.rows !== null
                )
            )
            .subscribe((pagination: GridData<any>) => {
                this.conversations.set(pagination.rows);

                // Actualizar el total de conversaciones disponibles
                this.totalConversations.set(pagination.total);

                // Reset flag después de procesar
                this.isLoadingMoreConversations.set(false);
            });

        // Obtener la lista inicial de conversaciones y setearla en conversations
        lastValueFrom(
            this.conversationService
            .pagination({
                scope: conversationListPaginationScope,
                query: {
                limit: this.currentConversationLimit(),
                order: [['updatedAt', 'desc']],
                },
                constraint: {
                where: {
                    accountId: this.currentAccount().id,
                },
                },
            })
        ).then((pagination: GridData<any>) => {
            this.conversations.set(pagination.rows);
            this.totalConversations.set(pagination.total);
        });
    }

    // Seleccionar una conversación
    selectConversation(conversation: ChatConversation): void
    {
        this.activeConversation.set(conversation);
        this.messages.set([]);
        this.currentMessageLimit.set(20); // Reset limit for new conversation
        this.totalMessages.set(0); // Reset total for new conversation
        this.actionService.action({
                    id          : 'ama::generalChatConversation.list.getMessages',
                    isViewAction: true,
                });
        // Set timeout to allow the messages to be loaded
        setTimeout(() => {
            this.showChatList.set(false);
        }, 500);
    }

    // Iniciar un nuevo chat
    startNewChat(): void
    {
        const newConversation = {
            id: uuid(),
            accountId: this.currentAccount().id,
            title: 'Nueva conversación',
        }
        this.activeConversation.set(newConversation);
        this.conversations
            .update(conversations => [newConversation, ...conversations]);

        this.messages.set([]);
        this.currentMessageLimit.set(20); // Reset limit for new conversation
        this.totalMessages.set(0); // Reset total for new conversation
        this.showChatList.set(false);
    }

    // Volver a la lista de conversaciones
    backToConversationList(): void
    {
        this.showChatList.set(true);
        this.actionService.action({
                    id          : 'ama::generalChatConversation.list.getConversations',
                    isViewAction: false,
                });
    }


    // Control de scroll separado para conversaciones y mensajes
    handlerScroll($event)
    {
        const element = $event.target as HTMLElement;

        if (this.showChatList()) {
            // Estamos en la lista de conversaciones - scroll hacia abajo para cargar más
            const threshold = 50;
            const scrollPosition = element.scrollTop + element.clientHeight;
            const scrollHeight = element.scrollHeight;

            if (scrollPosition >= scrollHeight - threshold && !this.isLoadingMoreConversations()) {
                // Verificar si ya tenemos todas las conversaciones disponibles
                if (this.currentConversationLimit() >= this.totalConversations()) {
                    console.log('Ya se han cargado todas las conversaciones disponibles');
                    return;
                }

                this.currentConversationLimit.update(limit => limit + 20);
                this.isLoadingMoreConversations.set(true);

                this.actionService.action({
                    id          : 'ama::generalChatConversation.list.getConversations',
                    isViewAction: false,
                });
            }
        } else {
            // Estamos en la vista de chat - scroll hacia arriba para cargar mensajes más antiguos
            if (element.scrollTop === 0 && !this.isLoadingMoreMessages() && this.activeConversation()) {
                // Verificar si ya tenemos todos los mensajes disponibles
                if (this.currentMessageLimit() >= this.totalMessages()) {
                    console.log('Ya se han cargado todos los mensajes disponibles');
                    return; // No hay más mensajes que cargar
                }

                this.currentMessageLimit.update(limit => limit + 20);
                this.isLoadingMoreMessages.set(true);

                this.actionService.action({
                    id          : 'ama::generalChatConversation.list.getMessages',
                    isViewAction: true,
                });
            }
        }
    }

    async handleAction(action: Action): Promise<void>
    {
        switch (action.id)
        {
            case 'ama::generalChatConversation.list.getMessages':
                    await lastValueFrom(
                        this.messageService
                            .pagination({
                                scope: generalChatConversationPaginationScope,
                                query: {
                                    limit: this.currentMessageLimit(),
                                    order: [['createdAt', 'desc']],
                                },
                                constraint: {
                                    where: {
                                        conversationId: this.activeConversation().id,
                                    },
                                },
                            })
                    );

                this.isLoading.set(false);
                break;

            case 'ama::generalChatConversation.list.getConversations':
                await lastValueFrom(
                    this.conversationService
                        .pagination({
                            scope: conversationListPaginationScope,
                            query: {
                                limit: this.currentConversationLimit(),
                                order: [['updatedAt', 'desc']],
                            },
                            constraint: {
                                where: {
                                    accountId: this.currentAccount().id,
                                },
                            },
                        })
                );
                this.isLoading.set(false);
                break;

            case 'ama::generalChatConversation.list.sendMessage':
                const messageId = uuid();
                const conversationId = this.activeConversation()?.id;

                this.messages
                    .update(messages => [...messages, {
                        id       : messageId,
                        accountId: this.currentAccount().id,
                        account  : this.currentAccount(),
                        conversationId,
                        role     : MessageRole.USER,
                        message  : action.meta.messageInput,
                        createdAt: now().format('YYYY-MM-DD HH:mm:ss'),
                    }]);

                this.isLoading.set(true);

                await lastValueFrom(
                    this.messageService
                        .sendMessage<any>({ //DigitaiCreateFleetMessage
                            object: {
                                id     : messageId,
                                conversationId,
                                message: action.meta.messageInput,
                            },
                        }),
                );

                this.actionService.action({
                    id          : 'ama::generalChatConversation.list.getMessages',
                    isViewAction: true,
                });
                this.isLoading.set(false);
                break;
        }
    }

    // Set User with Data from current account
    setUser(userData): void {
        this.currentUser.set({
            id: userData.id,
            name: userData.user?.name,
        });
    }

    /**
     * Maneja la navegación a una nota específica
     * @param event Objeto con noteId y noteName
     */
    onNavigateToNote(event: { noteId: string, noteName: string }): void
    {
        this.router.navigate([
            'ama/note/edit',
            event.noteId,
        ]);
    }

    // Acceso al botón de chat
    get showChatButton() {
        return this.chatService.showChatButton;
    }
}
